import React, { useEffect, useState } from "react";
import Brand from "@/components/Brand";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, Play, Pause, CheckCircle, XCircle, Info, Eye, Lock, Waveform } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GlassCard } from "@/components/glass/GlassCard";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1765164478579-2c041dad2c7d?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85";

function money(v, ccy = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(v || 0);
}

function StanceRow({ clause }) {
  const s = clause.stance || "neutral";
  const chipCls = s === "for" ? "chip-low" : s === "against" ? "chip-high" : "chip-neutral";
  return (
    <div className="p-4 rounded-lg border bg-[var(--surface)] border-[var(--border)]">
      <div className="flex items-center gap-2 mb-1">
        <span className={`chip ${chipCls}`}>{s}</span>
        <span className="text-xs text-[var(--text-3)]">{clause.category}</span>
      </div>
      <div className="text-sm font-medium mb-1">{clause.title}</div>
      <div className="text-xs text-[var(--text-2)] leading-relaxed">{clause.plain_language}</div>
    </div>
  );
}

export default function PublicBriefing() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [signed, setSigned] = useState(false);
  const audioRef = React.useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/public/share/${token}`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then(setData)
      .catch(() => setError("Invalid or expired link"));
  }, [token]);

  const fetchAudio = async () => {
    setAudioLoading(true);
    try {
      // Public briefing uses the shared contract id via authenticated owner session
      // For public access, we don't require auth on the audio; fallback: just show text
      toast.info("Public audio briefing requires signing in to the owner account for generation.");
    } finally { setAudioLoading(false); }
  };

  const onOtpChange = (i, v) => {
    const val = v.slice(-1).replace(/\D/g, "");
    const newOtp = [...otp];
    newOtp[i] = val;
    setOtp(newOtp);
    if (val && i < 5) document.getElementById(`otp-${i+1}`)?.focus();
  };

  const canSign = otp.every((d) => d.length === 1);

  const sign = () => {
    if (!canSign) return;
    setSigned(true);
    toast.success("Thank you — your acceptance has been recorded");
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center">
        <div>
          <XCircle size={48} className="text-rose-400 mx-auto mb-4" />
          <div className="text-xl font-medium mb-1">Link invalid</div>
          <div className="text-sm text-[var(--text-2)]">{error}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[var(--text-2)] text-sm animate-pulse">Loading briefing…</div>
      </div>
    );
  }

  const { contract, analysis } = data;

  return (
    <div className="min-h-screen relative">
      <div className="fixed inset-0 -z-10">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--bg)]/35 to-[var(--bg)]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-8 pb-16">
        <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
          <div className="flex items-center gap-3 mb-8">
            <Brand size={36} />
            <div>
              <div className="overline text-[var(--accent)]">Clariva — Public contract briefing</div>
              <div className="text-sm text-[var(--text-2)]">Read the AI breakdown before you decide.</div>
            </div>
          </div>
        </motion.div>

        {signed ? (
          <GlassCard strong className="p-12 text-center max-w-2xl mx-auto">
            <CheckCircle size={64} weight="fill" className="text-emerald-400 mx-auto mb-4" />
            <h1 className="text-3xl font-medium mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Signed with confidence</h1>
            <p className="text-[var(--text-2)]">A signed copy has been sent to both parties.</p>
          </GlassCard>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}>
              <GlassCard strong className="p-6 md:p-8 h-full">
                <div className="overline mb-2 flex items-center gap-2"><Sparkle size={12} weight="fill" className="text-[var(--accent)]" /> AI transparency briefing</div>
                <h1 className="text-3xl md:text-4xl font-medium tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{contract.title}</h1>
                <div className="text-sm text-[var(--text-2)] mb-6">From <strong>{contract.counterparty}</strong> · {money(contract.value_amount, contract.value_currency)}</div>

                {analysis ? (
                  <>
                    <div className="mb-6 text-lg leading-relaxed" style={{ fontFamily: 'var(--font-heading)', fontWeight: 400 }}>
                      {analysis.plain_language_summary}
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-6">
                      {["for", "neutral", "against"].map((s) => {
                        const count = (analysis.clauses || []).filter((c) => c.stance === s).length;
                        return (
                          <div key={s} className="text-center p-3 rounded-md bg-[var(--surface)] border border-[var(--border)]">
                            <div className="text-2xl font-mono font-semibold">{count}</div>
                            <div className={`text-xs uppercase tracking-wider mt-1 ${s === "for" ? "text-emerald-400" : s === "against" ? "text-rose-400" : "text-[var(--text-3)]"}`}>{s} you</div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-2">
                      {(analysis.clauses || []).map((c, i) => <StanceRow key={i} clause={c} />)}
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-[var(--text-2)] py-12 text-center">AI analysis not yet available for this contract.</div>
                )}
              </GlassCard>
            </motion.div>

            <motion.div initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }} className="space-y-6">
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="overline">Contract text</div>
                  <span className="chip chip-neutral"><Eye size={10} weight="fill" className="inline mr-1" /> Read-only</span>
                </div>
                <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-[var(--text-2)] max-h-[45vh] overflow-y-auto">
                  {contract.content}
                </pre>
              </GlassCard>

              <GlassCard strong className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-md bg-[var(--surface-strong)] border border-[var(--border-strong)] flex items-center justify-center">
                    <Lock size={16} weight="duotone" className="text-[var(--accent)]" />
                  </div>
                  <div>
                    <div className="overline">Sign with OTP</div>
                    <div className="text-xs text-[var(--text-3)]">Enter the 6-digit code sent to your email</div>
                  </div>
                </div>
                <div className="flex gap-2 mb-4">
                  {otp.map((d, i) => (
                    <Input key={i} id={`otp-${i}`} value={d} onChange={(e) => onOtpChange(i, e.target.value)} maxLength={1} inputMode="numeric" data-testid={`public-otp-${i}`} className="w-12 h-14 text-center text-xl font-mono glass-subtle border-[var(--border-strong)]" />
                  ))}
                </div>
                <div className="text-xs text-[var(--text-3)] mb-4">Demo: enter any 6 digits.</div>
                <Button onClick={sign} disabled={!canSign} data-testid="public-sign-btn" className="w-full h-12">Sign contract</Button>
                <div className="mt-4 text-xs text-[var(--text-3)] text-center">Views: {data.share.views} · Powered by Clariva · Audit trail encrypted</div>
              </GlassCard>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
